package com.sweetlime.bingeamigo.presentation.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.LibraryMovie
import com.sweetlime.bingeamigo.presentation.features.library.LibraryViewModel
import com.sweetlime.bingeamigo.util.computeDisplayRating
import com.sweetlime.bingeamigo.util.format1f
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MovieCard(
    libraryMovie: LibraryMovie,
    modifier: Modifier = Modifier,
    showActions: Boolean = false,
    communityRating: Double? = null,
    onRemove: () -> Unit = {},
    onClick: () -> Unit = {},
    libraryViewModel: LibraryViewModel = koinViewModel(),
) {
    val posterUrl = "https://image.tmdb.org/t/p/w500${libraryMovie.movie.posterPath}"

    val favorites by libraryViewModel.favorites.collectAsState()
    val isFavorite = favorites.any { it.id == libraryMovie.id }
    var isFavoriteLoading by remember { mutableStateOf(false) }

    LaunchedEffect(isFavorite) { isFavoriteLoading = false }

    Box(
        modifier = modifier
            .aspectRatio(2f / 3f)
            .clip(RoundedCornerShape(10.dp))
            .background(BrandColors.CardElevated)
            .clickable { onClick() }
    ) {
        KamelImage(
            resource = asyncPainterResource(posterUrl),
            contentDescription = libraryMovie.movie.title,
            contentScale = ContentScale.Crop,
            modifier = Modifier.fillMaxSize()
        )

        // Bottom gradient + title + rating
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomStart)
                .background(Brush.verticalGradient(colors = listOf(Color.Transparent, Color(0xD9000000))))
                .padding(8.dp)
        ) {
            Column {
                Text(
                    text = libraryMovie.movie.title,
                    color = Color.White,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(3.dp),
                    modifier = Modifier.padding(top = 2.dp)
                ) {
                    Icon(
                        Icons.Filled.Star,
                        contentDescription = null,
                        tint = BrandColors.Primary,
                        modifier = Modifier.size(10.dp)
                    )
                    Text(
                        text = computeDisplayRating(libraryMovie.movie.voteAverage, communityRating).format1f(),
                        color = BrandColors.Primary,
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }

        if (showActions) {
            // Remove button — top-right
            Box(
                modifier = Modifier
                    .padding(6.dp)
                    .size(22.dp)
                    .align(Alignment.TopEnd)
                    .background(Color(0x8C000000), CircleShape)
                    .clickable { onRemove() },
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Filled.Close,
                    contentDescription = "Remove",
                    tint = Color.White,
                    modifier = Modifier.size(12.dp)
                )
            }

            // Favorite button — top-left
            Box(
                modifier = Modifier
                    .padding(6.dp)
                    .size(22.dp)
                    .align(Alignment.TopStart)
                    .background(Color(0x8C000000), CircleShape)
                    .clickable {
                        if (!isFavoriteLoading) {
                            isFavoriteLoading = true
                            if (isFavorite) libraryViewModel.removeFromFavorites(libraryMovie)
                            else libraryViewModel.addToFavorites(libraryMovie)
                        }
                    },
                contentAlignment = Alignment.Center
            ) {
                if (isFavoriteLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(12.dp),
                        strokeWidth = 1.5.dp,
                        color = Color.White,
                    )
                } else {
                    Icon(
                        imageVector = if (isFavorite) Icons.Filled.Favorite else Icons.Outlined.FavoriteBorder,
                        contentDescription = if (isFavorite) "Remove from favorites" else "Add to favorites",
                        tint = if (isFavorite) BrandColors.Favorite else Color.White,
                        modifier = Modifier.size(12.dp)
                    )
                }
            }
        }
    }
}